import { FC, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectIdentity } from '../../store/entities/identity';
import { DetailHeader } from '../common/DetailHeader';
import { InvitationCreateStep1 } from './InvitationCreateStep1';
import { InvitationCreateStep2 } from './InvitationCreateStep2';
import { QcsMinimalistStepper } from './QcsMinimalistStepper';
import { invitationListActions } from '../../store/entities/invitationList';
import { getCompanySettings, selectCompanySettings, selectCompanySettingsState } from '../../store/entities/companySettings';
import { ShowResourceContent } from '../ShowResourceContent';
import { resources } from '@api/resources';

import { Identity } from '../../models/auth';
import { InvitationCreateForm } from '../../models/invitation';
import { invitationTypeVisitListActions } from '../../store/entities/invitationTypeVisitList';
import { useNavigation } from '../../utils/urlPaths';
import { DateTime } from 'luxon';
import { CompanySettingsDto } from '@api/models/settings/CompanySettingsDto';
import { invitationService } from '@api/services/services';
import { CreateUpdateInvitation } from '@api/models/invitation/CreateUpdateInvitation';
import { OneTimePersonDto } from '@api/models/invitation/OneTimePersonDto';
import { VisitationAddModalOneTimeConflict, VisitationAddOneTimeConflictData } from '../visitation/VisitationAddModalOneTimeConflict';
import { resolveVisitorConflicts } from './resolveVisitorConflicts';
import { useConflictResolver } from './useConflictResolver';
import { toOneTimePersonDto } from './oneTimePerson';

const TOTAL_STEPS = 2;

const getDefaultData = (identity: Identity, companySettings: CompanySettingsDto): InvitationCreateForm => ({
  isVisitor: false,
  employee: { id: identity.id, name: identity.name },
  establishments: [],
  workplaces: [],
  activities: [],
  additionalVisitors: [],
  disableVisitationNotification: companySettings.defaultDisableVisitationNotification ?? false,
  createHandoverProtocol: companySettings.defaultCreateHandoverProtocol ?? false,
  turnstilesActive: companySettings.defaultTurnstilesActive ?? false,
  validFrom: DateTime.now().toISODate(),
  addWorkerToInvitation: companySettings.defaultAddWorkerToInvitation ?? true,
  notifyEmployees: [],
  isNotifyEmployees: companySettings.defaultNotifyEmployees ?? false,
  visitorCompanyName: '',
  entryRequestRequired: companySettings.defaultEntryRequestRequired ?? false,
});

const InvitationCreateContent: FC = () => {
  const { getBackPath, goBack } = useNavigation('/invitation');
  const dispatch = useAppDispatch();
  const identity = useAppSelector(selectIdentity);
  const companySettings = useAppSelector(selectCompanySettings);

  const [stepNumber, setStepNumber] = useState(1);
  const [data, setData] = useState<InvitationCreateForm>(getDefaultData(identity, companySettings));
  const visitorConflictResolver = useConflictResolver<VisitationAddOneTimeConflictData<OneTimePersonDto>, OneTimePersonDto>();
  const isLastStep = () => stepNumber === TOTAL_STEPS;

  const getPersons = async (formData: InvitationCreateForm): Promise<OneTimePersonDto[] | undefined | null> => {
    if (!formData.isVisitor || !formData.primaryVisitor) return undefined;
    const initial: OneTimePersonDto[] = [toOneTimePersonDto(formData.primaryVisitor), ...(formData.additionalVisitors ?? []).map(toOneTimePersonDto)];
    const resolved = await resolveVisitorConflicts(initial, visitorConflictResolver.request);
    return resolved ? resolved.persons : null;
  };

  const handleSubmit = async (formData: InvitationCreateForm) => {
    if (!isLastStep()) {
      setData((prev) => ({ ...prev, ...formData }));
      setStepNumber((prev) => prev + 1);
      return;
    }

    const persons = await getPersons(formData);
    if (persons === null) return;

    const createData: CreateUpdateInvitation = {
      name: formData.name!,
      employee: formData.employee!,
      validFrom: formData.validFrom!,
      validTo: formData.validTo,
      workplaces: formData.workplaces!,
      activities: formData.activities!,
      responsiblePerson: formData.responsiblePerson,
      persons,
      supplier: formData.isVisitor ? undefined : formData.supplier,
      note: formData.note,
      noteInternal: formData.noteInternal,
      attachments: formData.attachments?.map((x) => x.id!),
      disableVisitationNotification: formData.disableVisitationNotification ?? false,
      createHandoverProtocol: formData.createHandoverProtocol ?? false,
      turnstilesActive: formData.turnstilesActive ?? false,
      notifyEmployees: !formData.disableVisitationNotification && formData.isNotifyEmployees ? formData.notifyEmployees : [],
      visitorCompanyName: formData.visitorCompanyName ?? '',
      earliestEntryTime: formData.earliestEntryTime,
      entryRequestRequired: formData.entryRequestRequired ?? false,
      addWorkerToInvitation: formData.addWorkerToInvitation,
    };

    await invitationService.create(createData);

    dispatch(invitationListActions.reload(true));
    dispatch(invitationTypeVisitListActions.reload(true));
    goBack();
  };

  const handleGoBack = () => {
    setStepNumber((prev) => prev - 1);
  };

  return (
    <>
      <VisitationAddModalOneTimeConflict data={visitorConflictResolver.conflict} onClose={visitorConflictResolver.close} />

      <DetailHeader title={'invitation.create.title'} backText={'common.cancel'} backTo={getBackPath} />

      <QcsMinimalistStepper stepNumber={stepNumber} totalSteps={TOTAL_STEPS} />

      {stepNumber === 1 && <InvitationCreateStep1 invitationData={data} handleSubmit={handleSubmit} />}

      {stepNumber === 2 && <InvitationCreateStep2 invitationData={data} handleGoBack={handleGoBack} handleSubmit={handleSubmit} />}
    </>
  );
};

export const InvitationCreate: FC = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getCompanySettings());
  }, [dispatch]);

  return (
    <ShowResourceContent resource={resources.getSettings} fetchStateSelector={selectCompanySettingsState}>
      <InvitationCreateContent />
    </ShowResourceContent>
  );
};
